/**
 * /control/sustituciones · M-12.
 */
import { useEffect, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import api from "../../lib/api";
import { SF_ESTADOS, fmtFecha } from "./_common";
import { Card, CardContent, CardHeader, CardTitle } from "../../components/ui/card";
import { Badge } from "../../components/ui/badge";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { Textarea } from "../../components/ui/textarea";
import {
    Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle, DialogTrigger,
} from "../../components/ui/dialog";
import {
    Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "../../components/ui/select";
import { toast } from "sonner";
import { Shuffle, Plus, FileDown, Check, X, AlertCircle } from "lucide-react";

export function SustitucionesList() {
    const [items, setItems] = useState([]);
    const [open, setOpen] = useState(false);
    const [form, setForm] = useState({
        fideicomiso_id: "", direccion: "entrada",
        nombre_fiduciaria: "", contacto: "", email: "",
        fecha_entrega: "", motivo: "",
    });
    const [fids, setFids] = useState([]);
    const nav = useNavigate();

    const load = () => api.get("/control/sustituciones").then(r => setItems(r.data));
    useEffect(() => {
        load();
        api.get("/fideicomisos").then(r => {
            const arr = Array.isArray(r.data) ? r.data : r.data.items || [];
            setFids(arr.map(f => ({ id: f.id, label: f.numero_fideicomiso || f.id })));
        }).catch(() => setFids([]));
    }, []);

    const crear = async () => {
        if (!form.fideicomiso_id || form.motivo.length < 10) {
            toast.error("Fideicomiso y motivo (≥10) requeridos"); return;
        }
        try {
            const now = new Date().toISOString();
            const r = await api.post("/control/sustituciones", {
                fideicomiso_id: form.fideicomiso_id,
                direccion: form.direccion,
                contraparte: {
                    nombre_fiduciaria: form.nombre_fiduciaria,
                    contacto: form.contacto, email: form.email,
                },
                fecha_decision_partes: now, fecha_notificacion: now,
                fecha_entrega_recepcion_prog: form.fecha_entrega
                    ? new Date(form.fecha_entrega).toISOString() : now,
                motivo: form.motivo,
            });
            toast.success(`Sustitución iniciada · ${r.data.folio_sustitucion}`);
            setOpen(false);
            nav(`/control/sustituciones/${r.data.id}`);
        } catch (e) { toast.error(e?.response?.data?.detail || "Error"); }
    };

    return (
        <div className="space-y-6" data-testid="sustituciones-list">
            <div className="flex items-start justify-between gap-3 flex-wrap">
                <div>
                    <h1 className="text-3xl font-serif font-semibold" style={{ fontFamily: "'Fraunces', serif" }}>
                        Sustituciones Fiduciarias · M-12
                    </h1>
                    <p className="text-sm text-muted-foreground">Entrada y salida de fideicomisos.</p>
                </div>
                <Dialog open={open} onOpenChange={setOpen}>
                    <DialogTrigger asChild>
                        <Button className="bg-[color:var(--coral)] hover:bg-[color:var(--coral)]/90 text-white"
                            data-testid="btn-nueva-sustitucion">
                            <Plus className="h-4 w-4 mr-1" /> Nueva sustitución
                        </Button>
                    </DialogTrigger>
                    <DialogContent className="max-w-xl">
                        <DialogHeader><DialogTitle>Nueva sustitución fiduciaria</DialogTitle></DialogHeader>
                        <div className="space-y-3">
                            <div className="grid grid-cols-2 gap-3">
                                <div>
                                    <label className="text-xs font-semibold">Dirección</label>
                                    <Select value={form.direccion}
                                        onValueChange={v => setForm({ ...form, direccion: v })}>
                                        <SelectTrigger><SelectValue /></SelectTrigger>
                                        <SelectContent>
                                            <SelectItem value="entrada">Entrada (recibimos)</SelectItem>
                                            <SelectItem value="salida">Salida (entregamos)</SelectItem>
                                        </SelectContent>
                                    </Select>
                                </div>
                                <div>
                                    <label className="text-xs font-semibold">Fideicomiso</label>
                                    <Select value={form.fideicomiso_id}
                                        onValueChange={v => setForm({ ...form, fideicomiso_id: v })}>
                                        <SelectTrigger data-testid="sel-fid-sf"><SelectValue placeholder="Seleccionar..." /></SelectTrigger>
                                        <SelectContent>
                                            {fids.map(f => <SelectItem key={f.id} value={f.id}>{f.label}</SelectItem>)}
                                        </SelectContent>
                                    </Select>
                                </div>
                            </div>
                            <div>
                                <label className="text-xs font-semibold">Nombre fiduciaria contraparte</label>
                                <Input value={form.nombre_fiduciaria}
                                    onChange={e => setForm({ ...form, nombre_fiduciaria: e.target.value })} />
                            </div>
                            <div className="grid grid-cols-2 gap-3">
                                <div><label className="text-xs font-semibold">Contacto</label>
                                    <Input value={form.contacto} onChange={e => setForm({ ...form, contacto: e.target.value })} /></div>
                                <div><label className="text-xs font-semibold">Email</label>
                                    <Input type="email" value={form.email} onChange={e => setForm({ ...form, email: e.target.value })} /></div>
                            </div>
                            <div>
                                <label className="text-xs font-semibold">Fecha entrega programada</label>
                                <Input type="date" value={form.fecha_entrega}
                                    onChange={e => setForm({ ...form, fecha_entrega: e.target.value })} />
                            </div>
                            <div>
                                <label className="text-xs font-semibold">Motivo</label>
                                <Textarea rows={3} value={form.motivo}
                                    onChange={e => setForm({ ...form, motivo: e.target.value })} />
                            </div>
                        </div>
                        <DialogFooter>
                            <Button variant="outline" onClick={() => setOpen(false)}>Cancelar</Button>
                            <Button onClick={crear} data-testid="btn-crear-sustitucion">Crear</Button>
                        </DialogFooter>
                    </DialogContent>
                </Dialog>
            </div>
            <Card>
                <CardContent className="p-0">
                    <table className="w-full text-sm">
                        <thead className="bg-muted/40 text-xs uppercase">
                            <tr>
                                <th className="px-3 py-2 text-left">Folio</th>
                                <th className="px-3 py-2 text-left">Dirección</th>
                                <th className="px-3 py-2 text-left">Contraparte</th>
                                <th className="px-3 py-2 text-left">Estado</th>
                                <th className="px-3 py-2 text-left">Programada</th>
                            </tr>
                        </thead>
                        <tbody>
                            {items.length === 0 && (
                                <tr><td colSpan={5} className="p-6 text-center text-muted-foreground">Sin sustituciones.</td></tr>
                            )}
                            {items.map(sf => {
                                const cfg = SF_ESTADOS[sf.estado] || { label: sf.estado, cls: "" };
                                return (
                                    <tr key={sf.id} className="border-t hover:bg-muted/20">
                                        <td className="px-3 py-2 font-mono text-xs">
                                            <Link to={`/control/sustituciones/${sf.id}`} className="text-primary hover:underline">
                                                {sf.folio_sustitucion}
                                            </Link>
                                        </td>
                                        <td className="px-3 py-2 text-xs uppercase">{sf.direccion}</td>
                                        <td className="px-3 py-2 text-xs">{sf.contraparte?.nombre_fiduciaria}</td>
                                        <td className="px-3 py-2"><Badge className={cfg.cls}>{cfg.label}</Badge></td>
                                        <td className="px-3 py-2 text-xs">{fmtFecha(sf.fecha_entrega_recepcion_prog)}</td>
                                    </tr>
                                );
                            })}
                        </tbody>
                    </table>
                </CardContent>
            </Card>
        </div>
    );
}

export function SustitucionDetalle() {
    const { id } = useParams();
    const [sf, setSf] = useState(null);
    const reload = () => api.get(`/control/sustituciones/${id}`).then(r => setSf(r.data));
    useEffect(() => { reload(); }, [id]);
    if (!sf) return <div className="p-6 text-muted-foreground">Cargando…</div>;

    const cfg = SF_ESTADOS[sf.estado] || { label: sf.estado, cls: "" };
    const total = sf.checklist_entrega_recepcion.length;
    const completos = sf.checklist_entrega_recepcion.filter(i => i.estado === "completo").length;
    const progreso = Math.round((completos / total) * 100);

    const toggleItem = async (idx, actual) => {
        const nuevo = actual === "completo" ? "pendiente" : "completo";
        try {
            await api.post(
                `/control/sustituciones/${id}/actualizar-item/${idx}`,
                null, { params: { estado: nuevo } },
            );
            reload();
        } catch (e) { toast.error(e?.response?.data?.detail || "Error"); }
    };
    const completar = async () => {
        try {
            await api.post(`/control/sustituciones/${id}/completar`);
            toast.success("Sustitución completada · Acta generada");
            reload();
        } catch (e) { toast.error(e?.response?.data?.detail || "Error completando"); }
    };
    const pdf = () => window.open(`${api.defaults.baseURL}/control/sustituciones/${id}/pdf`, "_blank");

    return (
        <div className="space-y-6" data-testid="sustitucion-detalle">
            <div className="flex items-center gap-2 flex-wrap">
                <h1 className="text-2xl font-serif font-semibold" style={{ fontFamily: "'Fraunces', serif" }}>
                    {sf.folio_sustitucion}
                </h1>
                <Badge className={cfg.cls}>{cfg.label}</Badge>
                <Badge className="bg-sky-100 text-sky-900 border-sky-300 border">{sf.direccion.toUpperCase()}</Badge>
                {sf.fideicomiso_espejo_id && (
                    <Badge className="bg-emerald-100 text-emerald-900 border-emerald-300 border"
                        data-testid="badge-espejo">
                        Fideicomiso espejo creado
                    </Badge>
                )}
            </div>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <Card className="md:col-span-2">
                    <CardHeader><CardTitle className="text-base">Contraparte</CardTitle></CardHeader>
                    <CardContent className="text-sm space-y-1">
                        <p><b>{sf.contraparte?.nombre_fiduciaria}</b></p>
                        <p className="text-xs">{sf.contraparte?.contacto} · {sf.contraparte?.email}</p>
                        <p className="text-xs text-muted-foreground mt-2">Motivo: {sf.motivo}</p>
                    </CardContent>
                </Card>
                <Card>
                    <CardHeader><CardTitle className="text-base">Acciones</CardTitle></CardHeader>
                    <CardContent className="space-y-2">
                        {sf.estado !== "completada" && (
                            <Button className="w-full bg-[color:var(--coral)] hover:bg-[color:var(--coral)]/90 text-white"
                                onClick={completar} disabled={completos < total}
                                data-testid="btn-completar-sf">
                                Completar sustitución
                            </Button>
                        )}
                        {sf.estado === "completada" && (
                            <Button className="w-full" variant="outline" onClick={pdf}
                                data-testid="btn-descargar-acta">
                                <FileDown className="h-4 w-4 mr-1" /> Descargar acta
                            </Button>
                        )}
                    </CardContent>
                </Card>
            </div>

            <Card>
                <CardHeader className="pb-2">
                    <CardTitle className="text-base flex items-center gap-2">
                        <Shuffle className="h-4 w-4" /> Checklist entrega-recepción
                        <span className="text-sm text-muted-foreground font-normal ml-2">
                            {completos}/{total} · {progreso}%
                        </span>
                    </CardTitle>
                </CardHeader>
                <CardContent>
                    <div className="h-2 w-full bg-muted rounded overflow-hidden mb-4">
                        <div className="h-full bg-emerald-500 transition-all" style={{ width: `${progreso}%` }} />
                    </div>
                    <ul className="space-y-2" data-testid="lista-checklist">
                        {sf.checklist_entrega_recepcion.map((it, idx) => (
                            <li key={idx} className="flex items-center gap-3 p-2 rounded hover:bg-muted/20">
                                <Button size="sm" variant={it.estado === "completo" ? "default" : "outline"}
                                    onClick={() => toggleItem(idx, it.estado)}
                                    disabled={sf.estado === "completada"}
                                    data-testid={`check-item-${idx}`}
                                    className={it.estado === "completo" ? "bg-emerald-600 hover:bg-emerald-700" : ""}>
                                    {it.estado === "completo" ? <Check className="h-4 w-4" /> : <X className="h-4 w-4" />}
                                </Button>
                                <div className="flex-1 text-sm">
                                    <p>{it.item}</p>
                                    {it.actualizado_at && (
                                        <p className="text-xs text-muted-foreground">
                                            {fmtFecha(it.actualizado_at)} · {it.actualizado_por}
                                        </p>
                                    )}
                                </div>
                                {it.estado === "observacion" && (
                                    <AlertCircle className="h-4 w-4 text-amber-600" />
                                )}
                            </li>
                        ))}
                    </ul>
                </CardContent>
            </Card>
        </div>
    );
}

export default SustitucionesList;
